import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { useState } from "react";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { FormControlLabel, RadioGroup } from "@mui/material";
function RecursiveConfirmationPopup({
  header,
  handleSubmit,
  handleClose,
  toggleIsRecursive,
  module,
  radioButtonLabels = [],
  headerTitle,
  additionalTextBeforeOptions = "",
  optionButtonsText = [],
  isRecursive,
  recursiveOverrideWarningMessage,
}: {
  header?: React.ReactNode;
  handleSubmit: () => void;
  handleClose: () => void;
  /** Wired directly to a checkbox `onChange`, so it receives the event, not a boolean. */
  toggleIsRecursive: (event: React.ChangeEvent<HTMLInputElement>) => void;
  module?: string;
  radioButtonLabels?: string[];
  /** Passed by `projects`' FormTemplate; forwarded, not read here. */
  engineType?: boolean;
  /** Callers pass a plain string (often `""`) as well as translated strings and elements. */
  headerTitle?: string | TranslatedString | React.ReactElement;
  /** Callers pass a plain string, a translated string, or a `<Trans>` element. */
  additionalTextBeforeOptions?: React.ReactNode;
  optionButtonsText?: string[];
  isRecursive?: boolean;
  /** Usually a `<Trans>` element carrying an interpolated resource name. */
  recursiveOverrideWarningMessage?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await handleSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };

  const modelContent = () => {
    return (
      <div className="flex flex-col gap-6">
        {additionalTextBeforeOptions && <span>{additionalTextBeforeOptions}</span>}
        <div className="flex flex-col gap-4">
          <RadioGroup onChange={toggleIsRecursive}>
            <FormControlLabel
              checked={!isRecursive}
              value="retain"
              control={<RadioButton />}
              label={
                radioButtonLabels[1] ||
                t("default_no_override_template", {
                  header: header,
                  module: module,
                })
              }
              sx={{ mb: "1rem" }}
            />
            <FormControlLabel
              value="overRide"
              checked={isRecursive}
              control={<RadioButton />}
              label={
                radioButtonLabels[0] ||
                t("default_yes_override_template", {
                  header: header,
                })
              }
            />
          </RadioGroup>
        </div>
        {isRecursive && (
          <div className="rounded-lg p-3 text-sm bg-banner_color_yellow_translucent">
            {recursiveOverrideWarningMessage}
          </div>
        )}
      </div>
    );
  };

  const footerContent = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={onSubmit}
        data-testid="nextAddUserBtn"
        loading={isSubmitting}
      >
        {optionButtonsText.length > 0 ? optionButtonsText[0] : t("Submit")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleClose} data-testid="cancelAddUserBtn">
        {optionButtonsText.length > 1 ? optionButtonsText[1] : t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      width="40%"
      // The modal's own prop is `TranslatedString | ReactElement`; callers of THIS component pass a
      // plain string (often `""`), so the fallback expression is asserted here. Erased.
      headerTitle={
        (headerTitle ||
          t("recurscive_confirmation_popup_default_header_text", {
            header: header,
            module: module,
          })) as TranslatedString
      }
      onCancel={handleClose}
      modelContent={modelContent()}
      footerContent={footerContent()}
    />
  );
}

export default RecursiveConfirmationPopup;
